import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Hook to automatically sign out users after a period of inactivity.
 * Default timeout is 30 minutes.
 */
export const useSessionTimeout = (timeoutMs: number = 30 * 60 * 1000) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetTimeout = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(async () => {
      console.log('Session timed out due to inactivity');
      try {
        await supabase.auth.signOut();
      } catch (e) {
        console.warn('Signout failed during timeout:', e);
      }
      window.location.href = '/login?reason=timeout';
    }, timeoutMs);
  }, [timeoutMs]);

  useEffect(() => {
    // Events that indicate user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    
    const handleActivity = () => resetTimeout();

    events.forEach(event => window.addEventListener(event, handleActivity));
    resetTimeout();

    return () => {
      events.forEach(event => window.removeEventListener(event, handleActivity));
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [resetTimeout]);
};
